import {UserData} from 'APIs/letrusAPI/user/types';
import {Term} from 'store/services/termsServices';
import {acceptTerm, privacyTerm, studentTerm} from 'utils/types/terms';

interface StudentTerms {
  privacyVersion?: string;
  studentVersion?: string;
}

export interface UseTermsProps {
  terms: Term[];
  userProfile: UserData['user']['user_profile'];
  userRoles: UserData['user_roles'];
  isContest?: boolean;
}

function useTerms({
  terms,
  userProfile,
  userRoles,
  isContest = false,
}: UseTermsProps) {
  const userTerms: StudentTerms = {
    privacyVersion: userProfile?.terms_privacy_version_id,
    studentVersion: userProfile?.terms_use_version_id,
  };

  const apiTerms = terms?.reduce<StudentTerms>((result, term) => {
    if (term.parameter === 'terms_use_version_id') {
      return {...result, studentVersion: term.value};
    }

    if (term.parameter === 'terms_privacy_version_id') {
      return {...result, privacyVersion: term.value};
    }

    return result;
  }, {});

  function getTermsToRender() {
    const [useTerm, contestTerm] = acceptTerm;
    const defaultTerm = isContest ? contestTerm : useTerm;
    const studentTermsList = [defaultTerm];

    if (userTerms.privacyVersion !== apiTerms.privacyVersion) {
      studentTermsList.push(privacyTerm);
    }

    if (userTerms.studentVersion !== apiTerms.studentVersion) {
      studentTermsList.push(studentTerm);
    }

    return studentTermsList;
  }

  const termsToRender = getTermsToRender();

  function isFirstAccess() {
    // Check if user is from EDE (pre school group)
    const isEDEUser = userRoles?.some((role) => role?.school_group_is_pre);

    // Check if user is from a normal school group
    const isNormalUser = !userRoles?.some((role) => role?.school_group_is_pre);

    // Check if EDE user has already accessed
    const hasPreAccessFlag = userProfile?.flag_pre_access === true;

    // Check if user has already accessed
    const hasFirstAccessFlag = userProfile?.flag_first_access === true;

    let isFirstAccess = false;

    if (isEDEUser && !isNormalUser) {
      // If user is only EDE, use pre-access flag
      isFirstAccess = !hasPreAccessFlag;
    } else {
      // Otherwise, use general first access flag
      isFirstAccess = !hasFirstAccessFlag;
    }

    const isUserComingFromExternalApi =
      sessionStorage.getItem('externalAPILogin');

    return isFirstAccess && !isUserComingFromExternalApi;
  }

  return {
    areTermsUpdated: termsToRender.length <= 1,
    userTerms: termsToRender,
    isFirstAccess,
  };
}

export default useTerms;
